import React from 'react';
import { isEqual } from 'lodash';
import { Timesheet } from 'api/report/types';
import { buildTimesheetPayload } from 'report/logic/timesheet/payload';
import { TimesheetReport } from 'utils/report';

interface Props {
    report: TimesheetReport;
    useSplitTextfields?: boolean;
    children: React.ReactNode;
}

interface TimesheetDraft {
    report: TimesheetReport;
    hasChanges: boolean;
    timesheet: Timesheet;
    setTimesheet: React.Dispatch<React.SetStateAction<Timesheet>>;
    enableSickHours: boolean;
    setEnableSickHours: React.Dispatch<React.SetStateAction<boolean>>;
    useSplitTextfields: boolean;
}

const TimesheetDraftContext = React.createContext<TimesheetDraft | null>(null);

export function TimesheetDraftProvider({
    report,
    useSplitTextfields = false,
    children,
}: Props) {
    const [timesheet, setTimesheet] = React.useState(() =>
        structuredClone(report.data)
    );
    const [enableSickHours, setEnableSickHours] = React.useState(
        report.summary.hasSickHours
    );

    // Disabled sickness fields remain in the draft, but are omitted on submission.
    const hasChanges = !isEqual(
        buildTimesheetPayload(timesheet, report.template.id, enableSickHours),
        buildTimesheetPayload(
            report.data,
            report.template.id,
            report.summary.hasSickHours
        )
    );

    // Reset the draft when source data changes while preserving display preferences.
    React.useEffect(() => {
        setTimesheet(structuredClone(report.data));
        setEnableSickHours(report.summary.hasSickHours);
    }, [report.data, report.summary]);

    return (
        <TimesheetDraftContext.Provider
            value={{
                report,
                hasChanges,
                timesheet,
                setTimesheet,
                enableSickHours,
                setEnableSickHours,
                useSplitTextfields,
            }}>
            {children}
        </TimesheetDraftContext.Provider>
    );
}

export function useTimesheetDraft(): TimesheetDraft {
    const draft = React.useContext(TimesheetDraftContext);
    if (!draft) {
        throw new Error(
            'Timesheet must be rendered inside TimesheetDraftProvider.'
        );
    }
    return draft;
}
